import { PathElement } from './path-element.ts';
import { MatchingContext } from '../path-pattern.ts';

import type { PathSegment } from '../../../../../types/path';

/*internal*/ export class CaptureVariablePathElement extends PathElement {
    readonly variableName: string
    readonly #constraintPattern?: RegExp;

    constructor(pos: number, captureDescriptor: Uint16Array, caseSensitive: boolean, separator: number) {
        super(pos, separator);
        let colon = -1;
        for (let i = 0; i < captureDescriptor.length; i++) {
            if (captureDescriptor[i] === 58) { // ':'
                colon = i;
                break;
            }
        }
        if (colon === -1) {
            // no constraint
            this.variableName = String.fromCharCode(...captureDescriptor.slice(1, captureDescriptor.length - 1));
            this.#constraintPattern = undefined;
        }
        else {
            this.variableName = String.fromCharCode(...captureDescriptor.slice(1, colon));
            const patternText = String.fromCharCode(...captureDescriptor.slice(colon + 1, captureDescriptor.length - 1));
            // always set dotall to true
            const flags = caseSensitive ? 's' : 'si';
            this.#constraintPattern = new RegExp(patternText, flags);
        }
    }

    matches(pathIndex: number, matchingContext: MatchingContext): boolean {
        if (pathIndex >= matchingContext.pathLength) {
            // no more path left to match this element
            return false;
        }
        const candidateCapture = matchingContext.pathElementValue(pathIndex);
        if (candidateCapture.length === 0) {
            return false;
        }

        if (this.#constraintPattern !== undefined) {
            // TODO possible optimization - only regex match if rest of pattern matches?
            const exec = this.#constraintPattern.exec(candidateCapture);
            if (exec === null) {
                return false;
            }
            // Ensure the match covers the entire string (full match)
            if (exec.index !== 0 || exec[0].length !== candidateCapture.length) {
                return false;
            }
            if (exec.length > 1) {
                throw new TypeError(`No capture groups allowed in the constraint regex: ${this.#constraintPattern}`)
            }
        }

        let match = false;
        pathIndex++;
        if (this.noMorePattern()) {
            if (matchingContext.determinedRemainingPath) {
                matchingContext.remainingPathIndex = pathIndex;
                match = true;
            }
            else {
                // needs to be at least one character
                match = (pathIndex === matchingContext.pathLength);
            }
        }
        else {
            if (this.next !== undefined) {
                match = this.next.matches(pathIndex, matchingContext);
            }
        }

        if (match && matchingContext.extractingVariables) {
            matchingContext.set(
                this.variableName,
                candidateCapture,
                (matchingContext.pathElements[pathIndex - 1] as PathSegment).parameters
            );
        }
        return match;
    }

    get normalizedLength() {
        return 1;
    }

    get chars(): Uint16Array {
        const sb: string[] = [];
        sb.push('{');
        sb.push(this.variableName);
        if (this.#constraintPattern !== undefined) {
            sb.push(':');
            sb.push(this.#constraintPattern.source);
        }
        sb.push('}');
        const s = sb.join('');
        const result = new Uint16Array(s.length);
        for (let i = 0; i < s.length; i++) {
            result[i] = s.charCodeAt(i);
        }
        return result;
    }

    get wildcardCount() {
        return 0;
    }

    get captureCount(): number {
        return 1;
    }

    get score(): number {
        return PathElement.CAPTURE_VARIABLE_WEIGHT;
    }

    toString() {
        return `CaptureVariable({${this.variableName}${this.#constraintPattern ? ':' + this.#constraintPattern.source : ''}})`;
    }
}
